/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ElementRef, ViewChild } from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  matAppsOutline,
  matBusinessOutline,
  matCheckOutline,
  matDevicesOutline,
  matPolicyOutline,
  matPowerSettingsNewOutline,
  matReceiptLongOutline,
  matSettingsOutline,
  matTransformOutline,
  matUnfoldMoreOutline,
} from '@ng-icons/material-icons/outline';

import { Store } from '@app/config/store';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Business_Context, Company, Entity, Gstin } from '@app/types/global.type';
import { lastValueFrom } from 'rxjs';
import { MgmtService } from '@app/services/mgmt.service';
import { getGstinList } from '@app/utils/gstin';
import { CommonService } from '@app/services/common.service';
import { ToastrService } from 'ngx-toastr';
import { Gstr1Service } from '@app/services/gstr1.service';
import { environment } from '@env/environment';

enum Apps {
  Einvois = 'taxpayer',
  Garnet = 'garnet',
  Settings = 'account',
}

export interface Gstin_Resp {
  companyId: string | number;
  gstnNo: string;
  parentCompany: string;
  rootCompanyId: string;
  companyName: string;
  cmpIsActive: number;
  isDeleted: number;
  isPrincipalPob: number;
  taxpayerType?: string;
}

@Component({
  selector: 'app-header, [app-header]',
  standalone: true,
  imports: [RouterModule, NgIconComponent, NgbDropdownModule],
  providers: [
    provideIcons({
      matBusinessOutline,
      matSettingsOutline,
      matAppsOutline,
      matUnfoldMoreOutline,
      matPowerSettingsNewOutline,
      matTransformOutline,
      matReceiptLongOutline,
      matPolicyOutline,
      matDevicesOutline,
      matCheckOutline,
    }),
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent {
  @ViewChild('menu') menu!: ElementRef;

  Apps = Apps;
  activeApp: Apps = Apps.Einvois;

  businessEntityList: Entity[] = [];
  activeEntity!: Entity | null;
  logoUrl = environment.LOGO_URL;

  constructor(
    protected store: Store,
    private ngbModal: NgbModal,
    private route: ActivatedRoute,
    private mgmtSvc: MgmtService,
    private router: Router,
    private commonSvc: CommonService,
    private toastr: ToastrService,
    private gstr1Svc: Gstr1Service,
  ) {
    this.initializeBusinessList();
    this.route.url.subscribe((url) => {
      this.activeApp = url[0].path as Apps;
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  openModal(content: any) {
    this.ngbModal.open(content, { centered: true, size: 'sm' });
  }

  async initializeBusinessList() {
    if (this.store.businessEntityList()?.length > 0) {
      this.businessEntityList = this.store.businessEntityList();
      this.businessEntityList = this.businessEntityList.sort((a, b) =>
        a.company.toString().localeCompare(b.company.toString()),
      );
    } else {
      this.businessEntityList = await lastValueFrom(this.mgmtSvc.getEntityList());
      this.businessEntityList = this.businessEntityList.sort((a, b) =>
        a.company.toString().localeCompare(b.company.toString()),
      );
      this.store.setBusinessEntityList(this.businessEntityList);
    }
    this.activeEntity =
      this.businessEntityList.find((item: Entity) => {
        return item.companyid == this.store.businessContext()?.rootCompanyId;
      }) ?? null;

    if (!this.activeEntity) {
      this.activeEntity = this.businessEntityList[0];
    }
    this.store.setSelectedEntity(this.activeEntity);
    this.updateGstinList();
  }

  switchBusiness(selectedEntity: Entity) {
    this.activeEntity = selectedEntity;
    this.store.setSelectedEntity(this.activeEntity);
    this.updateGstinList();
    this.changeCompany(this.activeEntity.companyid);
    this.mgmtSvc.resetRegistrations();
    this.gstr1Svc.setSelectedPan('');
    this.gstr1Svc.setSelectedGSTINOptions([]);
    this.router.navigate(['/sapphire']);
    this.ngbModal.dismissAll();
  }

  async updateGstinList() {
    if (this.store.user()?.email && this.activeEntity) {
      const gstinListResp = await lastValueFrom(
        this.mgmtSvc.getGstinList(this.store.user()?.email, this.activeEntity?.companyid),
      );
      const gstinListData = this.manipulategstinListData(gstinListResp);
      const businessContext = this.store.businessContext() as Business_Context;
      this.store.setGstinList(getGstinList(businessContext, gstinListData));
    }
    // this.store.setBusinessEntityList(this.businessEntityList);
  }

  changeCompany(companyid: string) {
    this.mgmtSvc.changeCompany({ companyid }).subscribe({
      next: (resp: any) => {
        if (resp && resp.status === 'SUCCESS') {
          this.toastr.success(resp.message);
          console.log(resp.response);

          const businessObj: Business_Context = {
            companyid: String(resp.response.companyid),
            companyName: '',
            rootCompanyName: resp.response[0]?.company ?? '',
            rootCompanyId: resp.response[0]?.rootCompanyid ?? '',
            gstinNo: '',
            registrationDate: '',
          };
          this.store.setBusiness(businessObj);
          this.fetchBusinessHierarchy(companyid);
          localStorage.removeItem('selectedPan');
          localStorage.removeItem('selectedGstin');
        }
      },
    });
  }

  manipulategstinListData(list: Gstin_Resp[]) {
    const gstinTempList: Gstin[] = [];
    list.forEach((item: Gstin_Resp) => {
      gstinTempList.push({
        rootCompanyName: '',
        companyId: item.companyId,
        label: item.companyName,
        value: item.gstnNo,
        rootCompanyId: item.rootCompanyId,
        checked: false,
        taxpayerType: item.taxpayerType ?? '',
      });
    });
    return gstinTempList;
  }

  fetchBusinessHierarchy(rootCompanyId: string) {
    this.mgmtSvc.getBusinessHierarchy({ companyid: rootCompanyId }).subscribe({
      next: (resp: any) => {
        const company: Company = resp.response ?? null;
        this.store.setCompany(company);
      },
    });
  }

  onLogoutClick() {
    this.store.logout();
    this.gstr1Svc.setSelectedPan('');
    this.gstr1Svc.setSelectedGSTINOptions([]);
  }
}
